import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(null);

  useEffect(() => {
    if (process.env.REACT_APP_PREVIEW_MODE === "true") { setLoading(false); return; }
    // Try to get token from localStorage on mount
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      tokenRef.current = savedToken;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const headers = {};
      if (tokenRef.current) {
        headers['Authorization'] = `Bearer ${tokenRef.current}`;
      }
      const response = await axios.get(`${API_URL}/api/admin/me`, {
        withCredentials: true,
        headers
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      tokenRef.current = null;
      localStorage.removeItem('admin_token');
    } finally {
      setLoading(false);
    }
  };

  const setUserData = (data) => {
    if (data?.access_token) {
      tokenRef.current = data.access_token;
      localStorage.setItem('admin_token', data.access_token);
    }
    setUser(data);
  };

  const getToken = () => tokenRef.current;

  const logout = async () => {
    try {
      const headers = {};
      if (tokenRef.current) {
        headers['Authorization'] = `Bearer ${tokenRef.current}`;
      }
      await axios.post(`${API_URL}/api/admin/logout`, {}, { withCredentials: true, headers });
    } catch (e) { /* ignore */ }
    setUser(null);
    tokenRef.current = null;
    localStorage.removeItem('admin_token');
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, checkAuth, setUserData, getToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
